import { useEffect } from 'react';

/**
 * Hook to detect user activity (mouse movement, keyboard input, window focus).
 * Calls the callback with true when user is active, false when idle.
 *
 * @param onActivityChange - Callback function that receives activity status
 * @param idleTimeout - Time in milliseconds before considering user idle (default: 60000ms = 1 minute)
 */
export const useActivityDetection = (
  onActivityChange: (active: boolean) => void,
  idleTimeout: number = 60000
) => {
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    let isActive = true;

    const resetTimer = () => {
      clearTimeout(timeout);

      // If user was inactive, notify they're now active
      if (!isActive) {
        isActive = true;
        onActivityChange(true);
      }

      // Set timer for idle detection
      timeout = setTimeout(() => {
        isActive = false;
        onActivityChange(false);
      }, idleTimeout);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, mark as inactive
        clearTimeout(timeout);
        isActive = false;
        onActivityChange(false);
      } else {
        // Tab is visible, reset timer
        resetTimer();
      }
    };

    // Initial timer
    resetTimer();

    // Event listeners for user activity
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('mousedown', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('scroll', resetTimer);
    window.addEventListener('touchstart', resetTimer);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('mousedown', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('scroll', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onActivityChange, idleTimeout]);
};
